import { useDeferredValue, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePaginatedQuery, useQuery } from "convex/react";
import { Ban, Braces, ChevronDown, ClipboardCheck, Copy, Database, KeyRound, Radar, Search, ShieldAlert, Users, X, type LucideIcon } from "lucide-react";
import { api } from "../../convex/_generated/api";
import { auditIdentity, buildAuditPresentation } from "../lib/auditDetails";
import { copyText } from "../lib/banTools";
import { ACTION_LABELS, ACTOR_LABELS, actionLabel, formatDate } from "../lib/format";
import { Badge, Button, Card, EmptyState, PageHeader, TableSkeleton } from "../components/ui";

const FAMILIES: Record<string, { icon: LucideIcon; tone: string }> = {
  blacklist: { icon: Ban, tone: "rose" }, suspect: { icon: ShieldAlert, tone: "warning" },
  check: { icon: ClipboardCheck, tone: "success" }, watch: { icon: Radar, tone: "rose" },
  user: { icon: Users, tone: "neutral" }, api_key: { icon: KeyRound, tone: "neutral" },
  api: { icon: KeyRound, tone: "neutral" }, migration: { icon: Database, tone: "neutral" },
};

export function AuditPage() {
  const [params, setParams] = useSearchParams();
  const search = params.get("q") ?? "";
  const deferredSearch = useDeferredValue(search.trim());
  const [actorType, setActorType] = useState<"all" | "user" | "api_key" | "system">("all");
  const [action, setAction] = useState("");
  const [feedback, setFeedback] = useState("");
  const searchStatus = useQuery(api.audit.searchStatus, {});
  const { results, status, loadMore } = usePaginatedQuery(api.audit.list, {
    actorType: actorType === "all" ? undefined : actorType,
    action: action || undefined, search: deferredSearch || undefined,
  }, { initialNumItems: 40 });
  const reset = () => { setParams({}, { replace: true }); setActorType("all"); setAction(""); setFeedback(""); };
  const copy = async (label: string, value: string) => {
    try { await copyText(value); setFeedback(`${label} copié.`); }
    catch { setFeedback("Copie impossible. Sélectionnez l’identifiant pour le copier manuellement."); }
  };

  return <div className="audit-journal">
    <PageHeader title="Logs" description="Retrouvez les actions, les joueurs concernés et les modifications enregistrées." />
    <Card className="audit-filters">
      <label className="field audit-search"><span>Rechercher dans le journal</span>
        <div className="audit-search__input"><Search size={18} aria-hidden="true" />
          <input className="input" type="search" aria-label="Rechercher dans les logs" placeholder="ID Discord, ID Epic, pseudo, motif ou tournoi…" maxLength={200}
            value={search} onChange={event => {
              const next = new URLSearchParams(params);
              if (event.target.value) next.set("q", event.target.value); else next.delete("q");
              setParams(next, { replace: true }); setFeedback("");
            }} />
        </div>
      </label>
      <label className="field"><span>Action</span>
        <select className="input" value={action} onChange={event => setAction(event.target.value)} aria-label="Filtrer par action">
          <option value="">Toutes les actions</option>
          {Object.entries(ACTION_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
      <label className="field"><span>Auteur</span>
        <select className="input" value={actorType} onChange={event => setActorType(event.target.value as typeof actorType)} aria-label="Filtrer par acteur">
          <option value="all">Tous les acteurs</option><option value="user">Utilisateurs</option>
          <option value="api_key">Clés API</option><option value="system">Système</option>
        </select>
      </label>
      {(search || action || actorType !== "all") && <Button variant="ghost" onClick={reset}><X size={15} aria-hidden="true" /> Réinitialiser</Button>}
      <p className="audit-filters__hint">{deferredSearch ? "Résultats par pertinence · anciens et nouveaux identifiants inclus" : "Du plus récent au plus ancien · tous les acteurs inclus par défaut"}</p>
    </Card>
    {searchStatus && !searchStatus.ready && <p className="notice">Indexation des anciens logs en cours. Les résultats de recherche sont encore partiels.</p>}
    <p className="audit-feedback" role="status" aria-live="polite">{feedback}</p>
    {status === "LoadingFirstPage" ? <TableSkeleton /> : results.length === 0 ? (
      <EmptyState title="Aucun log trouvé" description="Essayez un autre identifiant ou pseudo, ou retirez les filtres." action={<Button variant="secondary" onClick={reset}>Afficher tous les logs</Button>} />
    ) : <>
      <p className="audit-count">{results.length} événement{results.length > 1 ? "s" : ""} affiché{results.length > 1 ? "s" : ""}{status === "CanLoadMore" ? " · d’autres événements sont disponibles" : ""}</p>
      {deferredSearch && results.length >= 1024 && <p className="notice">La recherche est limitée à 1 024 résultats. Précisez l’identifiant, l’action ou l’auteur pour affiner.</p>}
      <Card className="audit-list">
        {results.map(entry => {
          const presentation = buildAuditPresentation(entry);
          const identity = auditIdentity(entry);
          const { icon: Icon, tone } = FAMILIES[entry.action.split(".")[0]] ?? { icon: Braces, tone: "neutral" };
          const danger = /\.(delete|deleted|failed|lifted|revoke|disable|purged)$/.test(entry.action);
          const playerQuery = identity.epicId || identity.discordId;
          const isPlayer = ["blacklist", "watch_alert", "suspect"].includes(entry.entityType ?? "") || entry.action === "api.check";
          const changeCount = presentation.details.filter(detail => detail.before !== undefined).length;
          return <article key={entry._id} className="audit-row">
            <span className={`audit-row__icon audit-row__icon--${danger ? "danger" : tone}`}><Icon size={18} aria-hidden="true" /></span>
            <div className="audit-row__main">
              <div className="audit-row__heading"><h2>{actionLabel(entry.action)}</h2>
                <div className="audit-row__attribution">
                  {entry.actorName ? <span className="actor">
                    {entry.actorAvatarUrl ? <img src={entry.actorAvatarUrl} alt="" /> : <span className="actor__fallback" aria-hidden="true">{entry.actorName.slice(0, 1).toUpperCase()}</span>}
                    <span>{entry.actorName}</span>
                  </span> : <Badge tone="neutral">{ACTOR_LABELS[entry.actorType] ?? entry.actorType}</Badge>}
                  <time dateTime={new Date(entry.createdAt).toISOString()}>{formatDate(entry.createdAt)}</time>
                </div>
              </div>
              <p className="audit-row__identity">{presentation.summary}</p>
              {(identity.epicId || identity.discordId) && <div className="audit-identifiers">
                {([["Epic ID", identity.epicId], ["Discord ID", identity.discordId]] as const).map(([label, value]) => value &&
                  <button key={label} type="button" className="audit-id" onClick={() => void copy(label, value)} aria-label={`Copier ${label} ${value}`}>
                    <span>{label}</span><code>{value}</code><Copy size={13} aria-hidden="true" />
                  </button>)}
                {isPlayer && playerQuery && <Link className="audit-dossier" to={`/blacklist?q=${encodeURIComponent(playerQuery)}`}>Dossier joueur</Link>}
              </div>}
              {presentation.details.length > 0 && <details className="audit-details">
                <summary aria-label={`Voir les détails : ${actionLabel(entry.action)}`}>
                  {changeCount ? `${changeCount} modification${changeCount > 1 ? "s" : ""} · avant / après` : "Voir les détails"} <ChevronDown size={14} aria-hidden="true" />
                </summary>
                <dl>{presentation.details.map((detail, index) => <div key={`${detail.label}-${index}`} className={detail.before !== undefined ? "audit-detail audit-detail--change" : "audit-detail"}>
                  <dt>{detail.label}</dt>
                  {detail.before !== undefined ? <dd className="audit-change">
                    <span><small>Avant</small><bdi>{detail.before}</bdi></span><span><small>Après</small><bdi>{detail.after}</bdi></span>
                  </dd> : <dd><bdi>{detail.value}</bdi></dd>}
                </div>)}</dl>
              </details>}
            </div>
          </article>;
        })}
        {status === "CanLoadMore" && <div className="load-more"><Button variant="secondary" onClick={() => loadMore(40)}>Afficher 40 événements de plus</Button></div>}
        {status === "LoadingMore" && <p className="load-more" role="status">Chargement des événements…</p>}
      </Card>
    </>}
  </div>;
}
